import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  CalendarDays,
  Clock3,
  DollarSign,
  Loader2,
  ReceiptText,
  RefreshCw,
  Store,
  Utensils,
  WalletCards,
} from 'lucide-react';
import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import WorkspaceLayout from '../../layout/WorkspaceLayout';
import BranchService from '../../services/branch/BranchService';
import { IBranchDropdown } from '../../types/branches/branchInterfaces';
import useRestaurantDashboard from '../../hooks/restaurant/useRestaurantDashboard';
import {
  DashboardPanel,
  KpiCard,
  RecentActiveOrders,
  SalesChange,
  SalesTrend,
} from '../../features/restaurant/dashboard/RestaurantDashboardComponents';
import {
  DiningRoomPanel,
  InventoryAlertsPanel,
  OperationPanel,
  ReservationsPanel,
  TopSellingPanel,
} from '../../features/restaurant/dashboard/RestaurantOperationalPanels';
import {
  formatDashboardDate,
  formatDashboardMoney,
  formatDashboardPercent,
  formatDashboardTime,
} from '../../features/restaurant/dashboard/restaurantDashboardFormatters';
import SelectField from '../../components/Forms/Input/SelectField';
import DatePicker from '../../components/Forms/DatePicker/DatePicker';
import RestaurantOnboardingHost from '../../features/restaurant/onboarding/RestaurantOnboardingHost';
import { useAuthUser } from '../../hooks/auth/useAuthUser';

const today = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

const DashboardSkeleton = () => (
  <div aria-label="Cargando dashboard" className="animate-pulse space-y-5">
    <div className="h-32 rounded-xl bg-gray-2 dark:bg-meta-4" />
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="h-40 rounded-xl bg-gray-2 dark:bg-meta-4" />
      ))}
    </div>
    <div className="h-[360px] rounded-xl bg-gray-2 dark:bg-meta-4" />
    <div className="grid gap-5 xl:grid-cols-2">
      <div className="h-96 rounded-xl bg-gray-2 dark:bg-meta-4" />
      <div className="h-96 rounded-xl bg-gray-2 dark:bg-meta-4" />
    </div>
  </div>
);

const RestaurantDashboard = () => {
  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [date, setDate] = useState(today);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [branchError, setBranchError] = useState<string | null>(null);
  const dashboard = useRestaurantDashboard(branchId, date);

  const loadBranches = () => {
    setLoadingBranches(true);
    setBranchError(null);
    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine) => [mine]);
    request
      .then((items) => {
        setBranches(items);
        setBranchId((current) => current || items[0]?.id || '');
      })
      .catch(() => setBranchError('No se pudieron cargar las sucursales.'))
      .finally(() => setLoadingBranches(false));
  };

  useEffect(loadBranches, [canPickBranch]);

  const data = dashboard.data;
  const historicalDate = date !== today();

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />
      <div className="mt-6 space-y-5 pb-20 xl:pb-10">
        <section className="rounded-xl border border-stroke bg-white px-5 py-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                Centro de operación
              </p>
              <h1 className="mt-1 text-2xl font-bold text-black dark:text-white">
                {data?.branchName || 'Dashboard del restaurante'}
              </h1>
              <p className="mt-1 text-sm text-bodydark2">
                Ventas del día y pulso operativo de la sucursal en un solo
                lugar.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              {canPickBranch && (
                <div className="min-w-[230px]">
                  <SelectField
                    label="Sucursal"
                    name="branchId"
                    value={branchId}
                    iconKey="building"
                    disabled={loadingBranches}
                    options={branches.map((branch) => ({
                      value: branch.id,
                      label: branch.name,
                    }))}
                    onChange={(event) => setBranchId(Number(event.target.value))}
                  />
                </div>
              )}

              <div className="min-w-[210px]">
                <DatePicker
                  label="Fecha de negocio"
                  name="businessDate"
                  value={date}
                  iconKey="calendar"
                  onChange={(value) => setDate(value)}
                />
              </div>

              <button
                type="button"
                onClick={() => void dashboard.refresh()}
                disabled={
                  !branchId || dashboard.loading || dashboard.refreshing
                }
                className="
                mb-5
                inline-flex
                h-[50px]
                items-center
                justify-center
                gap-2
                rounded-xl
                bg-primary
                px-5
                text-sm
                font-medium
                text-white
                transition
                hover:bg-primary/90
                disabled:cursor-not-allowed
                disabled:opacity-60
              "
              >
                <RefreshCw
                  className={`h-4 w-4 ${
                    dashboard.refreshing ? 'animate-spin' : ''
                  }`}
                />
                Actualizar
              </button>

              <div className="mb-5">
                <RestaurantOnboardingHost
                  branchId={typeof branchId === 'number' ? branchId : null}
                />
              </div>
            </div>
          </div>
          {data && (
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-stroke pt-4 text-xs text-bodydark2 dark:border-strokedark">
              <span className="inline-flex items-center gap-1.5">
                <CalendarClock className="h-4 w-4" />
                Datos del{' '}
                {formatDashboardDate(data.businessDate, { dateStyle: 'long' })}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock3 className="h-4 w-4" />
                Última actualización: {formatDashboardTime(data.generatedAt)}
              </span>
              {dashboard.refreshing && (
                <span className="inline-flex items-center gap-1.5 text-primary">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Actualizando…
                </span>
              )}
            </div>
          )}
        </section>

        {(branchError || dashboard.error) && (
          <div
            role="alert"
            className="flex flex-col gap-3 rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger sm:flex-row sm:items-center"
          >
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <p className="flex-1">
              <strong>No se pudo actualizar.</strong>{' '}
              {branchError || dashboard.error}
              {data && ' Se conservan los últimos datos disponibles.'}
            </p>
            <button
              onClick={
                branchError ? loadBranches : () => void dashboard.refresh()
              }
              className="rounded border border-danger/40 px-3 py-1.5 font-medium"
            >
              Reintentar
            </button>
          </div>
        )}

        {dashboard.loading && !data ? (
          <DashboardSkeleton />
        ) : !data ? (
          <DashboardPanel title="Dashboard no disponible">
            <div className="flex min-h-[300px] flex-col items-center justify-center px-6 text-center">
              <Store className="mb-3 h-10 w-10 text-bodydark2" />
              <p className="font-medium text-black dark:text-white">
                Seleccione una sucursal para comenzar
              </p>
              <p className="mt-1 text-sm text-bodydark2">
                Las métricas aparecerán cuando exista una sucursal activa.
              </p>
            </div>
          </DashboardPanel>
        ) : (
          <>
            {historicalDate && (
              <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-primary">
                Está consultando métricas históricas del{' '}
                {formatDashboardDate(date, { dateStyle: 'long' })}. Las tarjetas
                marcadas como “Ahora” muestran el estado operativo actual.
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
              <KpiCard
                label="Ventas cobradas"
                value={formatDashboardMoney(data.sales.netRevenue)}
                detail={<SalesChange value={data.sales.changePercentage} />}
                icon={<DollarSign className="h-5 w-5" />}
                tone="success"
              />
              <KpiCard
                label="Ticket promedio"
                value={formatDashboardMoney(data.sales.averageTicket)}
                detail={`${data.sales.acceptedTickets} cobros aceptados`}
                icon={<WalletCards className="h-5 w-5" />}
              />
              <KpiCard
                label="Órdenes activas"
                value={String(data.orders.activeNow)}
                detail="Ahora"
                icon={<ReceiptText className="h-5 w-5" />}
                href="/restaurant/operation/orders"
              />

              <KpiCard
                label="Ocupación"
                value={formatDashboardPercent(data.tables.occupancyRate)}
                detail={`${data.tables.occupied} mesas ocupadas · Ahora`}
                icon={<Utensils className="h-5 w-5" />}
                tone="warning"
                href="/restaurant/operation/tables"
              />

              <KpiCard
                label="Próximas reservas"
                value={String(data.reservations.upcoming)}
                detail="Pendientes hoy · Ahora"
                icon={<CalendarDays className="h-5 w-5" />}
                href="/restaurant/operation/reservations"
              />

              <KpiCard
                label="Alertas de inventario"
                value={String(data.inventory.lowStock)}
                detail={
                  <span
                    className={
                      data.inventory.outOfStock ? 'font-medium text-danger' : ''
                    }
                  >
                    {data.inventory.outOfStock} agotados · Ahora
                  </span>
                }
                icon={<AlertTriangle className="h-5 w-5" />}
                tone={data.inventory.outOfStock ? 'danger' : 'warning'}
                href="/restaurant/inventory/stock"
              />
            </div>
            <SalesTrend sales={data.sales} />
            <div className="grid gap-5 xl:grid-cols-2">
              <DiningRoomPanel tables={data.tables} />
              <OperationPanel orders={data.orders} kitchen={data.kitchen} />
            </div>
            <RecentActiveOrders
              orders={data.recentActiveOrders}
              branchId={data.branchId}
            />
            <div className="grid gap-5 xl:grid-cols-3">
              <ReservationsPanel reservations={data.reservations} />
              <TopSellingPanel items={data.topSellingItems} />
              <InventoryAlertsPanel inventory={data.inventory} />
            </div>
          </>
        )}
      </div>
    </WorkspaceLayout>
  );
};

export default RestaurantDashboard;
